import { ApolloError } from '@apollo/client';
import { useHingeHealthSecurityContext } from '@hinge-health/react-component-library';
import { useCallback, useEffect, useState } from 'react';
import {
  ProgramCapRefund,
  useGenerateProgramCapReportMutation,
  useGetClientInsurersByIdsLazyQuery,
  useGetProgramCapRefundByIdLazyQuery,
  useGetProgramCapsQuery,
  useUpdateProgramCapMutation,
} from '../../../types';
import { computeEffectiveRange, formatDate } from '../../../utils/date-helper';
import { ProgramCapFormValues } from '../form/validation-schema';

export interface ProgramCapRow extends ProgramCapRefund {
  id: string;
  clientName: string;
  effectiveDates: string;
  status: JSX.Element;
  loading?: boolean;
  correctedUserBasedLockPrice?: number | null;
}

interface UseProgramCapReturn {
  rows: ProgramCapRow[];
  loading: boolean;
  error: ApolloError | undefined;
  handleEdit: (values: ProgramCapFormValues) => Promise<void>;
  handleReport: (row: ProgramCapRow) => Promise<void>;
  generateReportLoading: boolean;
  generateReportError: ApolloError | undefined;
  updateProgramCapError: ApolloError | undefined;
  updateProgramCapSuccess: boolean;
  downloadReportError: Error | undefined;
  // Pagination metadata
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}

// ACCOUNTS-AND-PLAN-MIGRATION: clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
export const useProgramCap = (
  clientId: number | null,
  getStatusChip: (lockDate: string | null, startDate: string) => JSX.Element,
  onEditSuccess?: () => void,
): UseProgramCapReturn => {
  const [rowsMap, setRowsMap] = useState<Map<number, ProgramCapRow>>(new Map());
  const [updateProgramCapSuccess, setUpdateProgramCapSuccess] = useState(false);
  const [downloadReportError, setDownloadReportError] = useState<
    Error | undefined
  >(undefined);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { hingeHealthAuthState } = useHingeHealthSecurityContext();

  const [
    updateProgramCap,
    { data: updateProgramCapData, error: updateProgramCapError },
  ] = useUpdateProgramCapMutation();

  const [
    getClientInsurers,
    { data: clientData, loading: clientLoading, error: clientError },
  ] = useGetClientInsurersByIdsLazyQuery();

  const { data, loading, error } = useGetProgramCapsQuery({
    variables: {
      clientId,
      page: currentPage,
      size: pageSize,
    },
  });

  const [
    getProgramCapRefundById,
    {
      data: _getProgramCapRefundByIdData,
      loading: _getProgramCapRefundByIdLoading,
      error: _getProgramCapRefundByIdError,
    },
  ] = useGetProgramCapRefundByIdLazyQuery();

  const [
    generateReport,
    { loading: generateReportLoading, error: generateReportError },
  ] = useGenerateProgramCapReportMutation();

  const fetchRefundDataConcurrently = useCallback(
    (programCaps: Array<{ id: number }>) => {
      programCaps.forEach(programCap => {
        getProgramCapRefundById({
          variables: {
            programCapId: programCap.id,
          },
        })
          .then(result => {
            if (result.data?.getProgramCapRefundById) {
              const programCapId =
                result.data.getProgramCapRefundById.programCapId;
              if (!programCapId) return;

              setRowsMap(prevMap => {
                const existingRow = prevMap.get(programCapId);
                if (!existingRow) return prevMap;

                const updatedMap = new Map(prevMap);
                updatedMap.set(programCapId, {
                  ...existingRow,
                  ...result.data?.getProgramCapRefundById,
                  loading: false,
                });
                return updatedMap;
              });
            }
          })
          .catch(() => {
            setRowsMap(prevMap => {
              const existingRow = prevMap.get(programCap.id);
              if (!existingRow) return prevMap;

              const updatedMap = new Map(prevMap);
              updatedMap.set(programCap.id, {
                ...existingRow,
                loading: false,
              });
              return updatedMap;
            });
          });
      });
    },
    [getProgramCapRefundById],
  );

  const handleDownload = async (fileName: string): Promise<void> => {
    const token = hingeHealthAuthState?.accessToken?.accessToken;
    if (!token) {
      throw new Error('Unauthorized: No access token available');
    }

    const url = new URL(
      `${process.env.REACT_APP_BFF_ROOT}/billing/program-caps/report/stream`,
    );
    url.searchParams.append('fileName', fileName);

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/octet-stream',
      },
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  };

  const handleReport = async (row: ProgramCapRow): Promise<void> => {
    setDownloadReportError(undefined);
    try {
      if (!row.reportLink && row.programCapId) {
        await generateReport({
          variables: {
            generateProgramCapReportInput: {
              programCapId: row.programCapId,
            },
          },
        });

        const refundResponse = await getProgramCapRefundById({
          variables: {
            programCapId: row.programCapId,
          },
          fetchPolicy: 'network-only',
        });

        const updatedRefund = refundResponse.data?.getProgramCapRefundById;
        if (!updatedRefund?.reportLink) {
          throw new Error('Failed to generate report');
        }

        await handleDownload(updatedRefund.reportLink);
      } else if (row.reportLink) {
        await handleDownload(row.reportLink);
      }
    } catch (error) {
      setDownloadReportError(
        error instanceof Error
          ? error
          : new Error('Unknown error during report download'),
      );
    }
  };

  const handleEdit = async (values: ProgramCapFormValues): Promise<void> => {
    await updateProgramCap({
      variables: {
        updateProgramCapInput: {
          id: Number(values.programCapId),
          clientId: values.clientId,
          startDate: formatDate(values.startDate, 'YYYY-MM-DD'),
          endDate: formatDate(values.endDate, 'YYYY-MM-DD'),
          cap: values.cap,
        },
      },
      refetchQueries: ['GetProgramCaps'],
    });
  };

  useEffect(() => {
    if (data?.getProgramCaps) {
      setRowsMap(new Map());

      const uniqueClientIds = [
        ...new Set(
          data.getProgramCaps.items.map(programCap => programCap.clientId),
        ),
      ];

      getClientInsurers({
        variables: {
          clientIds: uniqueClientIds,
        },
      });

      setCurrentPage(data.getProgramCaps.page);
      setTotalPages(data.getProgramCaps.pages || 1);
      setTotalItems(data.getProgramCaps.total || 1);
      setPageSize(data.getProgramCaps.size);
    }
  }, [data, getClientInsurers, getProgramCapRefundById]);

  useEffect(() => {
    if (data?.getProgramCaps && clientData?.getClientInsurersByIds) {
      const clientNamesMap = clientData.getClientInsurersByIds.reduce(
        (acc, clientsInsurer) => {
          acc[clientsInsurer.client.id] = clientsInsurer.client.name;
          return acc;
        },
        {} as Record<number, string>,
      );

      const newRowsMap = new Map<number, ProgramCapRow>();
      data.getProgramCaps.items.forEach((programCap, index) => {
        newRowsMap.set(programCap.id, {
          id: index.toString(),
          clientName: clientNamesMap[programCap.clientId],
          effectiveDates: computeEffectiveRange(
            programCap.startDate,
            programCap.endDate,
          ),
          status: getStatusChip(
            programCap.lockDate ?? null,
            programCap.startDate,
          ),
          programCapId: programCap.id,
          loading: true,
          clientId: programCap.clientId,
          startDate: programCap.startDate,
          endDate: programCap.endDate,
          cap: programCap.cap,
          correctedUserBasedLockPrice: programCap.correctedUserBasedLockPrice,
          averageStickerPrice: 0,
          numMembers: 0,
          refundPerMember: 0,
          sumAmount: 0,
          totalRefundAmount: 0,
        });
      });
      setRowsMap(newRowsMap);

      fetchRefundDataConcurrently(data.getProgramCaps.items);
    }
  }, [data, clientData, getStatusChip, fetchRefundDataConcurrently]);

  useEffect(() => {
    if (updateProgramCapData) {
      setUpdateProgramCapSuccess(true);
      onEditSuccess?.();
    } else {
      setUpdateProgramCapSuccess(false);
    }
  }, [updateProgramCapData, onEditSuccess]);

  return {
    rows: Array.from(rowsMap.values()),
    loading: loading || clientLoading,
    error: error || clientError,
    handleEdit,
    handleReport,
    generateReportLoading,
    generateReportError,
    updateProgramCapError,
    updateProgramCapSuccess,
    downloadReportError,
    // Pagination metadata
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    setCurrentPage,
    setPageSize,
  };
};
